import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { message } from "antd";
import { Button, Col, Row } from "../../common/UIComponents";
import BaseModal from "../BaseModal";
import DateTimeSelector from "../../common/DateTimeSelector";
import { changeStatusOfOrders } from "../../api/OrdersApi";
import { alertMessage } from "../../common/Common";
import I18n from "../../common/I18n";
import { defaultOutboundDockDatetime } from "../../helpers/orders";

const initialDockDatetime = (order, existingDatetime) => {
  if (existingDatetime && moment(existingDatetime).isValid()) {
    return moment(existingDatetime);
  }
  return defaultOutboundDockDatetime(order || {});
};

const OutboundDockTimeModal = ({
  visible,
  orderId,
  orderNumber,
  order,
  existingDatetime,
  isMilitaryTime,
  onCancel,
  onSuccess,
}) => {
  const [outboundDockDatetime, setOutboundDockDatetime] = useState(() =>
    initialDockDatetime(order, existingDatetime)
  );
  const [inProgress, setInProgress] = useState(false);
  const [updateArmed, setUpdateArmed] = useState(false);

  useEffect(() => {
    if (!visible) {
      setUpdateArmed(false);
      return undefined;
    }
    setOutboundDockDatetime(initialDockDatetime(order, existingDatetime));
    setUpdateArmed(false);
    const timer = setTimeout(() => setUpdateArmed(true), 400);
    return () => clearTimeout(timer);
  }, [visible]);

  const handleOutboundDockChange = (part, value) => {
    if (!value) return;
    const current = outboundDockDatetime ? moment(outboundDockDatetime) : moment();
    const updated =
      part === "date"
        ? moment(value).hour(current.hour()).minute(current.minute())
        : moment(current).hour(moment(value).hour()).minute(moment(value).minute());
    setOutboundDockDatetime(updated);
  };

  const handleUpdate = () => {
    if (!updateArmed || !outboundDockDatetime || !orderId) return;
    setInProgress(true);
    const data = {
      order_ids: [orderId],
      status: "COMPLETED",
      exception_code: "",
      exception_message: "",
      deletion_reason: "",
      warehouse_id: window.localStorage.getItem("selectedWarehouses"),
      outbound_dock_datetime: `${moment(outboundDockDatetime).format("YYYY-MM-DD")} ${moment(outboundDockDatetime).format("HH:mm")}`,
    };
    changeStatusOfOrders(data)
      .then((result) => {
        setInProgress(false);
        if (result.success) {
          alertMessage(I18n.t("messages.processed"), 10);
          onSuccess();
          return;
        }
        const firstError = result.errors?.[0];
        const errorMessage =
          typeof firstError === "string"
            ? firstError
            : firstError?.errors?.[0] || "Failed to update status.";
        message.error(errorMessage);
      })
      .catch(() => {
        setInProgress(false);
        message.error("An error occurred while updating the status.");
      });
  };

  if (!visible) return null;

  return (
    <BaseModal
      title={orderNumber ? `Change Status of #${orderNumber}` : I18n.t("general.outbound_dock_time")}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}
      width={520}
      bodyStyle={{ padding: 20 }}
    >
      <Row gutter={16} type="flex">
        <Col xs={24}>
          <DateTimeSelector
            dateKey="outbound_dock_date"
            dateProps={{
              format: "Do MMM YYYY",
              label: I18n.t("general.outbound_dock_time"),
              require: true,
              value: outboundDockDatetime,
              onChange: (date) => handleOutboundDockChange("date", date),
              style: { width: "100%", marginBottom: "8px" },
              size: "small",
              allowClear: false,
            }}
            timeProps={{
              format: isMilitaryTime ? "HH:mm" : "hh:mm A",
              label: "Time",
              require: true,
              value: outboundDockDatetime,
              onChange: (time) => handleOutboundDockChange("time", time),
              style: { width: "100%" },
              size: "small",
              allowClear: false,
              isMilitaryTime,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} style={{ paddingTop: 16, textAlign: "center" }}>
          <Button type="danger" onClick={onCancel} data-testid="cancel-outbound-dock-button">
            Cancel
          </Button>
          &nbsp;&nbsp;
          <Button
            type="primary"
            icon="save"
            loading={inProgress}
            disabled={!updateArmed || !outboundDockDatetime}
            onClick={handleUpdate}
            data-testid="update-outbound-dock-button"
          >
            Update
          </Button>
        </Col>
      </Row>
    </BaseModal>
  );
};

OutboundDockTimeModal.propTypes = {
  visible: PropTypes.bool,
  orderId: PropTypes.string,
  orderNumber: PropTypes.string,
  order: PropTypes.object,
  existingDatetime: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isMilitaryTime: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

OutboundDockTimeModal.defaultProps = {
  visible: false,
  orderId: "",
  orderNumber: "",
  order: {},
  existingDatetime: null,
  isMilitaryTime: false,
};

export default OutboundDockTimeModal;
